import { LitElement, html } from 'lit'
import { ModalBaseMixin } from '../core/mixins/modal-base.mixin.js'

/**
 * Modal para gerenciar premium de um usuário (uso administrativo)
 * Suporta dois modos:
 * - Ativar: para usuários sem premium (selecionar plano e duração)
 * - Editar: para usuários premium (alterar plano, duração ou desativar)
 * Segue o padrão dos demais modais do projeto (Bulma + Lit)
 * Utiliza tokens CSS do Design System para cores e estilos visuais
 *
 * Aplica ModalBaseMixin para integração com History API (popstate),
 * botão voltar iOS e evento padronizado modal-closed.
 */
class ModalPremium extends ModalBaseMixin(LitElement) {
  static properties = {
    open: { type: Boolean, reflect: true },
    userData: { type: Object },
    loading: { type: Boolean },
    isEditMode: { type: Boolean }
  }

  createRenderRoot () {
    return this
  }

  constructor () {
    super()
    this.open = false
    this.userData = null
    this.loading = false
    this.isEditMode = false
  }

  /**
   * Abre o modal com os dados do usuÃ¡rio
   * Detecta automaticamente se Ã© modo ediÃ§Ã£o (usuÃ¡rio jÃ¡ premium) ou ativaÃ§Ã£o
   * @param {Object} user - Dados do usuÃ¡rio (uid, email, displayName, premium, plan, premiumExpires)
   */
  show (user) {
    this.userData = user
    this.isEditMode = user.premium === true
    this.open = true
    this.loading = false

    // Preencher valores apÃ³s renderizaÃ§Ã£o
    setTimeout(() => {
      const planSelect = this.querySelector('[data-cy="select-plano"]')
      const durationInput = this.querySelector('[data-cy="input-duracao"]')

      if (this.isEditMode && user.plan) {
        // Modo ediÃ§Ã£o: preencher com dados atuais
        if (planSelect) planSelect.value = user.plan || 'monthly'
        if (durationInput) {
          if (user.plan === 'lifetime') {
            durationInput.value = ''
            durationInput.disabled = true
          } else if (user.premiumExpires) {
            // Calcular dias restantes
            const now = new Date()
            const expires = new Date(user.premiumExpires)
            const daysLeft = Math.max(0, Math.ceil((expires - now) / (1000 * 60 * 60 * 24)))
            durationInput.value = String(daysLeft)
          } else {
            durationInput.value = '30'
          }
        }
      } else {
        // Modo ativaÃ§Ã£o: valores padrÃ£o
        if (planSelect) planSelect.value = 'monthly'
        if (durationInput) {
          durationInput.value = '30'
          durationInput.disabled = false
        }
      }
    }, 0)
  }

  /**
   * Fecha o modal e limpa estado.
   *
   * Limpa dados do usuário e flags de estado antes de delegar
   * para super.close() do ModalBaseMixin que seta open=false,
   * dispara modal-closed e gerencia o history state.
   *
   * @param {object} [options={}] - Opções de fechamento repassadas ao mixin base
   * @param {boolean} [options.fromPopstate=false] - Indica se veio do popstate
   */
  close (options = {}) {
    this.userData = null
    this.loading = false
    this.isEditMode = false
    super.close(options)
  }

  /**
   * Fecha o modal ao clicar no backdrop (Ã¡rea escurecida)
   * Delega para this.close() que jÃ¡ gerencia o estado do modal
   */
  _handleBackdropClick () {
    this.close()
  }

  /**
   * Impede propagaÃ§Ã£o de clique no card (evita fechar ao clicar dentro)
   * @param {Event} e - Evento de clique
   */
  _stopPropagation (e) {
    e.stopPropagation()
  }

  /**
   * Atualiza a duraÃ§Ã£o automaticamente ao trocar o plano
   * Planos tÃªm duraÃ§Ãµes padrÃ£o: monthly=30, quarterly=90, yearly=365, lifetime=null
   * @param {Event} e - Evento de change do select
   */
  _onPlanChange (e) {
    const plan = e.target.value
    const durationInput = this.querySelector('[data-cy="input-duracao"]')
    const durationMap = {
      monthly: '30',
      quarterly: '90',
      yearly: '365',
      lifetime: ''
    }
    if (durationInput) {
      durationInput.value = durationMap[plan] || '30'
      durationInput.disabled = plan === 'lifetime'
    }
  }

  /**
   * Dispara evento de ativaÃ§Ã£o/atualizaÃ§Ã£o com os dados do formulÃ¡rio
   * Emite custom event 'premium-activate' com detail contendo uid, plan e duration
   */
  _handleActivate () {
    const planSelect = this.querySelector('[data-cy="select-plano"]')
    const durationInput = this.querySelector('[data-cy="input-duracao"]')

    const plan = planSelect?.value || 'monthly'
    const duration = plan === 'lifetime' ? null : (parseInt(durationInput?.value) || 30)

    this.loading = true

    this.dispatchEvent(new CustomEvent('premium-activate', {
      detail: {
        uid: this.userData.uid,
        plan,
        duration
      },
      bubbles: true
    }))
  }

  /**
   * Dispara evento de desativaÃ§Ã£o de premium
   * Emite custom event 'premium-deactivate' com uid do usuÃ¡rio
   */
  _handleDeactivate () {
    if (!confirm(`Desativar premium de ${this.userData.displayName || this.userData.email}?`)) {
      return
    }

    this.loading = true

    this.dispatchEvent(new CustomEvent('premium-deactivate', {
      detail: {
        uid: this.userData.uid
      },
      bubbles: true
    }))
  }

  render () {
    const title = this.isEditMode ? 'Editar Premium' : 'Ativar Premium'
    const confirmText = this.isEditMode ? 'Salvar' : 'Ativar Premium'
    const confirmLoadingText = this.isEditMode ? 'Salvando...' : 'Ativando...'

    return html`
      <div
        class="modal ${this.open ? 'is-active' : ''}"
        @click="${this._handleBackdropClick}"
        data-cy="modal-premium"
        style="
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 20000;
          display: ${this.open ? 'flex' : 'none'};
          align-items: center;
          justify-content: center;
        "
      >
        <div
          class="card"
          @click="${this._stopPropagation}"
          style="
            width: 90%;
            max-width: 500px;
            max-height: 90vh;
            overflow: hidden;
            border-radius: var(--radius-card, 20px);
            box-shadow: var(--shadow-card, 0 10px 40px rgba(0,0,0,0.35));
            background-color: var(--bg-surface, #151C30);
            position: relative;
          "
        >
          <!-- O !important no style inline é necessário para vencer a regra genérica
               .modal .card-header que também usa !important no style.css.
               O modal premium é exceção: mantém seu gradiente próprio (--fill-premium). -->
          <header class="card-header" style="background: var(--fill-premium) !important; border-bottom: 1px solid var(--border, #26304A);">
            ${this.renderBotaoVoltarIOS()}
            <p class="card-header-title" data-cy="modal-premium-title" style="color: var(--text-primary, #F4F7FF);">
              ${title}
            </p>
            ${this.renderBotaoFechar('modal-premium-close')}
          </header>

          <div class="card-content custom-scrollbar" style="overflow-y: auto; overflow-x: hidden; max-height: calc(90vh - 52px); color: var(--text-primary, #F4F7FF);">
            <!-- Dados do usuÃ¡rio -->
            ${this.userData ? html`
              <div class="box p-3 mb-4" style="background: var(--bg-surface-secondary, #1D2740); border: 1px solid var(--border, #26304A);" data-cy="premium-user-info">
                <div class="has-text-weight-medium" style="color: var(--text-primary, #F4F7FF);">
                  ${this.userData.displayName || this.userData.email}
                </div>
                <div class="is-size-7" style="color: var(--text-secondary, #A8B3CF);">${this.userData.email}</div>
                ${this.isEditMode ? html`
                  <div class="is-size-7 mt-1" style="color: var(--text-secondary, #A8B3CF);">
                    Premium desde: ${this.userData.premiumSince ? new Date(this.userData.premiumSince).toLocaleDateString('pt-BR') : 'â€”'}
                  </div>
                ` : ''}
              </div>
            ` : ''}

            <!-- Plano -->
            <div class="field mb-4">
              <label class="label is-size-7 has-text-weight-medium" style="color: var(--text-primary, #F4F7FF);">Plano</label>
              <div class="control">
                <div class="select is-fullwidth">
                  <select data-cy="select-plano" @change="${this._onPlanChange}">
                    <option value="monthly">Mensal (30 dias)</option>
                    <option value="quarterly">Trimestral (90 dias)</option>
                    <option value="yearly">Anual (365 dias)</option>
                    <option value="lifetime">VitalÃ­cio (sem expiraÃ§Ã£o)</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- DuraÃ§Ã£o em dias -->
            <div class="field mb-4">
              <label class="label is-size-7 has-text-weight-medium" style="color: var(--text-primary, #F4F7FF);">DuraÃ§Ã£o (dias)</label>
              <div class="control">
                <input
                  class="input"
                  type="number"
                  min="1"
                  placeholder="30"
                  data-cy="input-duracao"
                />
              </div>
              <p class="help" style="color: var(--text-secondary, #A8B3CF);">Personalize a duraÃ§Ã£o ou use o padrÃ£o do plano</p>
            </div>

            <!-- BotÃ£o desativar (apenas em modo ediÃ§Ã£o) -->
            ${this.isEditMode ? html`
              <div class="field mb-4">
                <button
                  class="button is-danger is-outlined is-fullwidth"
                  type="button"
                  style="box-sizing: border-box; border-color: var(--danger, #EF4444); color: var(--danger, #EF4444);"
                  data-cy="btn-desativar-premium"
                  @click="${this._handleDeactivate}"
                  ?disabled="${this.loading}"
                >
                  <span class="icon"><i class="fas fa-times-circle"></i></span>
                  <span>Desativar Premium</span>
                </button>
              </div>
            ` : ''}

            <!-- BotÃµes -->
            <div class="field is-grouped">
              <div class="control is-expanded">
                <button
                  class="button is-fullwidth"
                  type="button"
                  data-cy="btn-cancelar-premium"
                  @click="${this.close}"
                  ?disabled="${this.loading}"
                >
                  Cancelar
                </button>
              </div>
              <div class="control is-expanded">
                <button
                  class="button is-fullwidth"
                  type="button"
                  data-cy="btn-confirmar-premium"
                  @click="${this._handleActivate}"
                  ?disabled="${this.loading}"
                  style="background-color: var(--cta-primary, #FF8A3D); color: var(--text-primary, #F4F7FF); border: none;"
                >
                  ${this.loading ? confirmLoadingText : confirmText}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `
  }
}

customElements.define('modal-premium', ModalPremium)
